import React, { useEffect, useRef, useState, useMemo } from "react";
import Globe, { type GlobeInstance } from "globe.gl";
import type {
  ActiveLayer,
  Entity,
  Event,
  Relationship,
} from "@nexus/contracts";
import { LAYER_METADATA, REL_TYPE_LABELS } from "../types";
import { Maximize2, RotateCw, Layers, Compass, ZoomIn, ZoomOut } from "lucide-react";

interface GlobeViewProps {
  entities: Entity[];
  events: Event[];
  relationships: Relationship[];
  activeLayers: Set<ActiveLayer>;
  selectedEntityId: string | null;
  selectedEventId: string | null;
  onSelectEntity: (entityId: string) => void;
  onSelectEvent: (event: Event) => void;
}

interface ArcItem {
  id: string;
  startLat: number;
  startLng: number;
  endLat: number;
  endLng: number;
  color: string | [string, string];
  stroke: number;
  dashLength: number;
  dashGap: number;
  dashAnimateTime: number;
  label: string;
  type: "event" | "relationship";
  event?: Event;
  relationship?: Relationship;
  isHighlighted: boolean;
}

interface RingItem {
  id: string;
  lat: number;
  lng: number;
  color: string;
  maxRadius: number;
  propagationSpeed: number;
  repeatPeriod: number;
  event: Event;
  isHighlighted: boolean;
}

interface PointItem extends Entity {
  color: string;
  radius: number;
  altitude: number;
  isHighlighted: boolean;
}

export const GlobeView: React.FC<GlobeViewProps> = ({
  entities,
  events,
  relationships,
  activeLayers,
  selectedEntityId,
  selectedEventId,
  onSelectEntity,
  onSelectEvent,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const globeInstanceRef = useRef<GlobeInstance | null>(null);
  const [autoRotate, setAutoRotate] = useState<boolean>(true);

  // Quick lookup map for entities
  const entityMap = useMemo(() => {
    const map = new Map<string, Entity>();
    for (const e of entities) {
      map.set(e.id, e);
    }
    return map;
  }, [entities]);

  // Filter events by active layers
  const filteredEvents = useMemo(() => {
    return events.filter((ev) =>
      ev.layers.some((l) => activeLayers.has(l as ActiveLayer)),
    );
  }, [events, activeLayers]);

  // 1. Prepare Points Data (Entities)
  const pointsData: PointItem[] = useMemo(() => {
    return entities.map((entity) => {
      const isSelected = selectedEntityId === entity.id;
      let color = "#38bdf8"; // default cyan for countries

      if (entity.type === "company") {
        color = "#c084fc"; // purple for tech giants
      } else if (entity.type === "alliance") {
        color = "#34d399"; // emerald for alliances
      } else if (entity.type === "org") {
        color = "#fbbf24"; // amber for orgs
      }

      if (isSelected) {
        color = "#ffffff";
      }

      return {
        ...entity,
        color,
        radius: isSelected ? 1.6 : entity.indexEligible ? 1.0 : 0.65,
        altitude: isSelected ? 0.06 : 0.02,
        isHighlighted: isSelected,
      };
    });
  }, [entities, selectedEntityId]);

  // 2. Prepare Rings Data (Event beacons / pulses)
  const ringsData: RingItem[] = useMemo(() => {
    return filteredEvents
      .map((ev) => {
        let lat = ev.geo?.lat;
        let lng = ev.geo?.lon;

        if (lat === undefined || lng === undefined) {
          const actor = entityMap.get(ev.actorId);
          if (actor) {
            lat = actor.lat;
            lng = actor.lon;
          }
        }

        if (lat === undefined || lng === undefined) return null;

        const primaryLayer = ev.layers.find((l) => activeLayers.has(l as ActiveLayer)) as
          | ActiveLayer
          | undefined;
        const layerColor = primaryLayer ? LAYER_METADATA[primaryLayer]?.color : "#fb7185";
        const isSelected = selectedEventId === ev.id;
        const hasSelection = Boolean(selectedEventId || selectedEntityId);

        let color = layerColor;
        if (isSelected) {
          color = "#ffffff";
        } else if (hasSelection) {
          // Dim down unselected event rings slightly
          color = `${layerColor}88`;
        }

        return {
          id: ev.id,
          lat,
          lng,
          color,
          maxRadius: isSelected ? 8.5 : Math.min(6.0, Math.max(2.5, ev.severity * 1.2)),
          propagationSpeed: isSelected ? 4.5 : 2.0,
          repeatPeriod: isSelected ? 700 : 1600,
          event: ev,
          isHighlighted: isSelected,
        };
      })
      .filter((r): r is RingItem => r !== null);
  }, [filteredEvents, activeLayers, entityMap, selectedEventId, selectedEntityId]);

  // 3. Prepare Arcs Data (Actor -> Targets for Events + Active Relationships)
  const arcsData: ArcItem[] = useMemo(() => {
    const arcs: ArcItem[] = [];
    const hasActiveSelection = Boolean(selectedEntityId || selectedEventId);

    // Add Event Arcs (Actor -> TargetIds)
    for (const ev of filteredEvents) {
      const actor = entityMap.get(ev.actorId);
      if (!actor) continue;

      const primaryLayer = ev.layers.find((l) => activeLayers.has(l as ActiveLayer)) as
        | ActiveLayer
        | undefined;
      const baseColor = primaryLayer ? LAYER_METADATA[primaryLayer]?.color : "#818cf8";
      const isSelectedEvent = selectedEventId === ev.id;
      const isEntityRelated =
        selectedEntityId &&
        (ev.actorId === selectedEntityId || ev.targetIds.includes(selectedEntityId));

      const isFocused = isSelectedEvent || isEntityRelated;

      for (const targetId of ev.targetIds) {
        const target = entityMap.get(targetId);
        if (!target) continue;

        // Dynamic Arc Styling based on Selection Focus (Priority 2)
        let arcColor: [string, string];
        let stroke: number;
        let dashAnimateTime: number;

        if (isFocused) {
          arcColor = ["#ffffff", baseColor];
          stroke = 2.8;
          dashAnimateTime = 1200;
        } else if (hasActiveSelection) {
          // Strongly dim unselected arcs
          arcColor = ["rgba(255, 255, 255, 0.05)", "rgba(255, 255, 255, 0.02)"];
          stroke = 0.6;
          dashAnimateTime = 3600;
        } else {
          // Default subtle state
          arcColor = [baseColor, "rgba(255, 255, 255, 0.2)"];
          stroke = 1.2;
          dashAnimateTime = 2200;
        }

        arcs.push({
          id: `ev-${ev.id}-${targetId}`,
          startLat: actor.lat,
          startLng: actor.lon,
          endLat: target.lat,
          endLng: target.lon,
          color: arcColor,
          stroke,
          dashLength: isFocused ? 0.45 : 0.35,
          dashGap: isFocused ? 0.15 : 0.25,
          dashAnimateTime,
          label: `<div style="background:rgba(12,19,34,0.95);border:1px solid rgba(255,255,255,0.2);padding:8px 12px;border-radius:6px;font-family:sans-serif;font-size:12px;box-shadow:0 4px 12px rgba(0,0,0,0.5);">
            <div style="font-weight:700;color:${baseColor};">[사건 경로] ${ev.titleKo}</div>
            <div style="color:#e2e8f0;margin-top:2px;">${actor.nameKo} ➔ ${target.nameKo}</div>
          </div>`,
          type: "event",
          event: ev,
          isHighlighted: Boolean(isFocused),
        });
      }
    }

    // Add Relationship Arcs when an entity is selected
    if (selectedEntityId) {
      for (const rel of relationships) {
        if (rel.fromId === selectedEntityId || rel.toId === selectedEntityId) {
          const from = entityMap.get(rel.fromId);
          const to = entityMap.get(rel.toId);
          if (!from || !to) continue;

          const relMeta = REL_TYPE_LABELS[rel.relType] || {
            labelKo: rel.relType,
            color: "#38bdf8",
          };

          arcs.push({
            id: `rel-${rel.id}`,
            startLat: from.lat,
            startLng: from.lon,
            endLat: to.lat,
            endLng: to.lon,
            color: [relMeta.color, "#ffffff"],
            stroke: 3.0,
            dashLength: relMeta.dash ? 0.35 : 0.7,
            dashGap: relMeta.dash ? 0.25 : 0.1,
            dashAnimateTime: 1400,
            label: `<div style="background:rgba(12,19,34,0.95);border:1px solid ${relMeta.color};padding:8px 12px;border-radius:6px;font-family:sans-serif;font-size:12px;box-shadow:0 0 16px ${relMeta.color}44;">
              <div style="font-weight:700;color:${relMeta.color};">[관계: ${relMeta.labelKo}] 강도 ${rel.strength}/5</div>
              <div style="color:#ffffff;margin-top:2px;">${from.nameKo} ➔ ${to.nameKo}</div>
            </div>`,
            type: "relationship",
            relationship: rel,
            isHighlighted: true,
          });
        }
      }
    }

    return arcs;
  }, [filteredEvents, activeLayers, entityMap, selectedEventId, selectedEntityId, relationships]);

  // Initialize Globe
  useEffect(() => {
    if (!containerRef.current) return;

    containerRef.current.innerHTML = "";

    const globe = new (Globe as any)(containerRef.current)
      .globeImageUrl("//unpkg.com/three-globe/example/img/earth-night.jpg")
      .bumpImageUrl("//unpkg.com/three-globe/example/img/earth-topology.png")
      .backgroundColor("rgba(0, 0, 0, 0)")
      .showAtmosphere(true)
      .atmosphereColor("#3b82f6")
      .atmosphereAltitude(0.24)
      // Points
      .pointsData([])
      .pointLat("lat")
      .pointLng("lon")
      .pointColor("color")
      .pointRadius("radius")
      .pointAltitude("altitude")
      .pointLabel(
        (d: any) => `
        <div style="background:rgba(11, 18, 33, 0.96);border:1px solid rgba(255,255,255,0.25);padding:9px 13px;border-radius:6px;color:#fff;font-family:sans-serif;font-size:12px;box-shadow:0 6px 16px rgba(0,0,0,0.6);">
          <div style="font-weight:700;color:#38bdf8;font-size:13px;">${d.nameKo} (${d.nameEn})</div>
          <div style="font-size:11px;color:#94a3b8;margin-top:3px;">유형: ${d.type.toUpperCase()} · [${d.lat.toFixed(2)}°, ${d.lon.toFixed(2)}°]</div>
          <div style="font-size:10px;color:#cbd5e1;margin-top:4px;">${d.indexEligible ? "★ 지수 산출 대상 국가" : "기업/비교역 실체"}</div>
        </div>
      `,
      )
      .onPointClick((point: any) => {
        onSelectEntity(point.id);
      })
      // Rings
      .ringsData([])
      .ringLat("lat")
      .ringLng("lng")
      .ringColor("color")
      .ringMaxRadius("maxRadius")
      .ringPropagationSpeed("propagationSpeed")
      .ringRepeatPeriod("repeatPeriod")
      // Arcs
      .arcsData([])
      .arcStartLat("startLat")
      .arcStartLng("startLng")
      .arcEndLat("endLat")
      .arcEndLng("endLng")
      .arcColor("color")
      .arcStroke("stroke")
      .arcDashLength("dashLength")
      .arcDashGap("dashGap")
      .arcDashAnimateTime("dashAnimateTime")
      .arcLabel("label")
      .onArcClick((arc: any) => {
        if (arc.event) {
          onSelectEvent(arc.event);
        }
      });

    // Camera initial point of view: East Asia & Pacific angle (Lat 28, Lng 120, Alt 2.2)
    globe.pointOfView({ lat: 28, lng: 120, altitude: 2.2 }, 1000);

    // Auto rotate
    const controls = globe.controls();
    if (controls) {
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.45;
      controls.enableDamping = true;
      controls.dampingFactor = 0.1;
    }

    globeInstanceRef.current = globe;

    const handleResize = () => {
      if (containerRef.current && globe) {
        globe.width(containerRef.current.clientWidth);
        globe.height(containerRef.current.clientHeight);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Update Dynamic Data to Globe
  useEffect(() => {
    const globe = globeInstanceRef.current;
    if (!globe) return;

    globe.pointsData(pointsData);
    globe.ringsData(ringsData);
    globe.arcsData(arcsData);
  }, [pointsData, ringsData, arcsData]);

  // Handle camera transition when entity is selected
  useEffect(() => {
    const globe = globeInstanceRef.current;
    if (!globe || !selectedEntityId) return;

    const entity = entityMap.get(selectedEntityId);
    if (entity) {
      globe.pointOfView(
        { lat: entity.lat, lng: entity.lon, altitude: 1.8 },
        1200,
      );
    }
  }, [selectedEntityId, entityMap]);

  // Handle camera transition when event is selected
  useEffect(() => {
    const globe = globeInstanceRef.current;
    if (!globe || !selectedEventId) return;

    const ev = events.find((e) => e.id === selectedEventId);
    if (ev) {
      let lat = ev.geo?.lat;
      let lng = ev.geo?.lon;
      if (lat === undefined || lng === undefined) {
        const actor = entityMap.get(ev.actorId);
        if (actor) {
          lat = actor.lat;
          lng = actor.lon;
        }
      }
      if (lat !== undefined && lng !== undefined) {
        globe.pointOfView({ lat, lng, altitude: 1.5 }, 1200);
      }
    }
  }, [selectedEventId, events, entityMap]);

  // Toggle Auto Rotation
  const toggleAutoRotate = () => {
    const globe = globeInstanceRef.current;
    if (!globe) return;
    const controls = globe.controls();
    if (controls) {
      controls.autoRotate = !autoRotate;
      setAutoRotate(!autoRotate);
    }
  };

  const resetCamera = () => {
    const globe = globeInstanceRef.current;
    if (!globe) return;
    globe.pointOfView({ lat: 28, lng: 120, altitude: 2.2 }, 1000);
  };

  const zoomIn = () => {
    const globe = globeInstanceRef.current;
    if (!globe) return;
    const pov = globe.pointOfView();
    globe.pointOfView({ altitude: Math.max(0.6, pov.altitude - 0.4) }, 400);
  };

  const zoomOut = () => {
    const globe = globeInstanceRef.current;
    if (!globe) return;
    const pov = globe.pointOfView();
    globe.pointOfView({ altitude: Math.min(4.0, pov.altitude + 0.4) }, 400);
  };

  return (
    <div className="globe-viewport">
      {/* 3D Container */}
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          cursor: "grab",
        }}
      />

      {/* Top Left HUD */}
      <div className="globe-hud-overlay">
        <div className="hud-pill font-mono">
          <Layers size={13} color="var(--accent-indigo)" />
          <span>ACTIVE EVENTS: <strong style={{ color: "#ffffff" }}>{filteredEvents.length}</strong></span>
        </div>
        <div className="hud-pill font-mono">
          <Compass size={13} color="var(--accent-cyan)" />
          <span>DISPLAYED ARCS: <strong style={{ color: "#ffffff" }}>{arcsData.length}</strong></span>
        </div>
      </div>

      {/* Bottom Right Controls */}
      <div className="globe-controls-overlay">
        <button
          className="btn-icon"
          onClick={toggleAutoRotate}
          title={autoRotate ? "자전 멈춤" : "자동 자전 시작"}
          style={{ color: autoRotate ? "var(--accent-cyan)" : "var(--text-muted)" }}
        >
          <RotateCw size={15} />
        </button>
        <button className="btn-icon" onClick={zoomIn} title="확대">
          <ZoomIn size={15} />
        </button>
        <button className="btn-icon" onClick={zoomOut} title="축소">
          <ZoomOut size={15} />
        </button>
        <button className="btn-icon" onClick={resetCamera} title="기본 시점 초기화">
          <Maximize2 size={15} />
        </button>
      </div>
    </div>
  );
};
